import { useMutation } from '@apollo/client';
import React, { useState } from 'react';

import {
  HStack,
  Heading,
  Input,
  NewButton,
  Paragraph,
  VStack,
  useToast,
} from '@eluve/components';
import { useAssignedTenantIdFromParams } from '@eluve/session-helpers';
import { useTenantLogo, useTenantName } from '@eluve/smart-blocks';

import { updateTenantDetailsMutation } from './operations';

export const TenantNameSettings: React.FC = () => {
  const toast = useToast();
  const tenantId = useAssignedTenantIdFromParams();
  const tenantName = useTenantName(tenantId);
  const { tenantLogoUrl } = useTenantLogo(tenantId);
  const [isEditing, setIsEditing] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentName, setCurrentName] = useState(tenantName ?? '');

  const [updateTenantDetails] = useMutation(updateTenantDetailsMutation, {
    onError: () => {
      toast.error('Failed to update organization name');
      setCurrentName(tenantName ?? '');
    },
    optimisticResponse: (data) => ({
      updateTenantsByPk: {
        __typename: 'Tenants' as const,
        id: tenantId,
        name: data.name ?? '',
        logoUrl: tenantLogoUrl,
      },
    }),
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCurrentName(value);
  };

  const handleSave = async () => {
    if (!currentName) {
      return;
    }

    if (currentName === tenantName) {
      setIsEditing(false);
      return;
    }

    try {
      setIsUpdating(true);

      await updateTenantDetails({
        variables: {
          tenantId,
          name: currentName,
          logoUrl: tenantLogoUrl,
        },
      });

      setIsEditing(false);
    } catch {
      // Error is handled by onError in useMutation
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setCurrentName(tenantName ?? '');
    setIsEditing(false);
  };

  return (
    <VStack gap={4}>
      <VStack>
        <Heading size="md">Organization Name</Heading>
        <Paragraph color="secondary" size="sm">
          This name will be displayed throughout the application.
        </Paragraph>
      </VStack>
      <HStack>
        <Input
          value={currentName}
          onChange={handleNameChange}
          className="w-72 rounded-lg border border-border-secondary bg-white"
          placeholder="Enter organization name"
          disabled={isUpdating || !isEditing}
        />
        {isEditing ? (
          <>
            <NewButton
              text="Cancel"
              type="outline"
              onClick={handleCancel}
              disabled={isUpdating}
            />
            <NewButton
              text="Save"
              type="primary"
              onClick={handleSave}
              disabled={
                isUpdating || !currentName || currentName === tenantName
              }
            />
          </>
        ) : (
          <NewButton
            text="Edit"
            type="outline"
            onClick={() => setIsEditing(true)}
          />
        )}
      </HStack>
    </VStack>
  );
};
